"use client"

export interface CookieOptions {
  expires?: Date | string
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: "strict" | "lax" | "none"
  httpOnly?: boolean
  maxAge?: number
}

export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  if (typeof document === "undefined") return

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

  if (options.expires) {
    const expires = typeof options.expires === "string" ? options.expires : options.expires.toUTCString()
    cookieString += `; expires=${expires}`
  }

  if (options.maxAge !== undefined) {
    cookieString += `; max-age=${options.maxAge}`
  }

  if (options.path) {
    cookieString += `; path=${options.path}`
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`
  }

  if (options.secure) {
    cookieString += `; secure`
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`
  }

  if (options.httpOnly) {
    cookieString += `; httponly`
  }

  document.cookie = cookieString
}

export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null

  const nameEQ = encodeURIComponent(name) + "="
  const cookies = document.cookie.split(";")

  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length))
    }
  }

  return null
}

export const removeCookie = (name: string, options: Pick<CookieOptions, "path" | "domain"> = {}): void => {
  setCookie(name, "", {
    ...options,
    expires: new Date(0),
    maxAge: 0,
  })
}

export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null
}

export const getAllCookies = (): Record<string, string> => {
  if (typeof document === "undefined") return {}

  const cookies: Record<string, string> = {}

  document.cookie.split(";").forEach(cookie => {
    const [name, value] = cookie.trim().split("=")
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value)
    }
  })

  return cookies
}
